/**
 * CR-5563: KPI reporting-period control for the supplier Performance tab.
 *
 * The ticket's complaint was that percentages were shown with no way to tell
 * what window they covered, so the caption beside the dropdown is as much the
 * feature as the dropdown itself — it names the exact dates every figure on
 * the tab is computed from.
 */
import { CalendarRange, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  PERFORMANCE_PERIOD_OPTIONS,
  PerformancePeriod,
  PerformanceWindow,
  describePerformancePeriod,
} from './performancePeriods';

interface PerformancePeriodFilterProps {
  period: PerformancePeriod;
  periodWindow: PerformanceWindow;
  loading?: boolean;
  onChange: (period: PerformancePeriod) => void;
}

export function PerformancePeriodFilter({
  period,
  periodWindow,
  loading = false,
  onChange,
}: PerformancePeriodFilterProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <CalendarRange className="h-4 w-4 text-muted-foreground" />
        <label htmlFor="performance-period" className="text-sm font-medium">
          Time Period
        </label>
      </div>

      <Select value={period} onValueChange={(v) => onChange(v as PerformancePeriod)}>
        <SelectTrigger id="performance-period" className="w-[200px]" aria-label="KPI reporting period">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERFORMANCE_PERIOD_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <p className="text-xs text-muted-foreground" aria-live="polite">
        Performance based on {describePerformancePeriod(period, periodWindow)}
      </p>

      {loading && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Recalculating…
        </span>
      )}
    </div>
  );
}

export default PerformancePeriodFilter;
